import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { AuthenticatedLayout } from '../components/AuthenticatedLayout';
import { api } from '../lib/api';
import type { SubscriptionInfo, SubscriptionTier } from '@wrm/shared';
import styles from './SubscriptionPage.module.css';

interface PricingTier {
  tier: SubscriptionTier;
  name: string;
  price: number;
  description: string;
  features: string[];
}

const tiers: PricingTier[] = [
  {
    tier: 'starter',
    name: 'Starter',
    price: 29,
    description: 'For individuals monitoring a single website',
    features: [
      '1 website',
      'Unlimited scans',
      'Weekly scan frequency',
      'Detailed reports',
      'Email alerts',
      '1 year data retention',
    ],
  },
  {
    tier: 'professional',
    name: 'Professional',
    price: 79,
    description: 'For professionals managing multiple websites',
    features: [
      '5 websites',
      'Unlimited scans',
      'Daily scan frequency',
      'Detailed reports',
      'Email alerts',
      '2 years data retention',
    ],
  },
  {
    tier: 'enterprise',
    name: 'Enterprise',
    price: 199,
    description: 'For organizations with extensive web presence',
    features: [
      '25 websites',
      'Unlimited scans',
      'On-demand scanning',
      'Detailed reports',
      'Email alerts',
      'Unlimited data retention',
    ],
  },
];

export function SubscriptionPage() {
  const { getToken } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  const loadSubscription = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;

      const sub = await api.getSubscriptionStatus(token);
      setSubscription(sub);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const handleUpgrade = async (tier: SubscriptionTier) => {
    setActionLoading(true);
    setError('');

    try {
      const token = await getToken();
      if (!token) return;

      const { checkoutUrl } = await api.createCheckoutSession(tier, token);
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? Your access will continue until the end of your current billing period.')) {
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const token = await getToken();
      if (!token) return;

      await api.cancelSubscription(token);
      loadSubscription();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const currentTierIndex = subscription
    ? tiers.findIndex(t => t.tier === subscription.tier)
    : -1;

  return (
    <AuthenticatedLayout>
      <div className={styles.header}>
        <h1 className={styles.title}>Subscription</h1>
        <p className={styles.subtitle}>Manage your plan and billing</p>
      </div>

      {loading && <p className={styles.loading}>Loading...</p>}

      {error && <p className={styles.error}>{error}</p>}

      {!loading && subscription && (
        <>
          <div className={styles.currentPlan}>
            <h2 className={styles.sectionTitle}>Current Plan</h2>
            <div className={styles.planCard}>
              <div className={styles.planInfo}>
                <span className={styles.planName}>
                  {subscription.tier ? subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1) : 'Free'}
                </span>
                <span className={styles.planStatus}>
                  {subscription.status === 'active' ? 'Active' :
                    subscription.status === 'cancelled' ? 'Cancelled' :
                      subscription.status}
                </span>
              </div>
              <div className={styles.planDetails}>
                <div className={styles.planDetail}>
                  <span className={styles.detailLabel}>Websites</span>
                  <span className={styles.detailValue}>{subscription.websiteLimit}</span>
                </div>
                <div className={styles.planDetail}>
                  <span className={styles.detailLabel}>Scan Frequency</span>
                  <span className={styles.detailValue}>
                    {subscription.scanFrequency ? subscription.scanFrequency.charAt(0).toUpperCase() + subscription.scanFrequency.slice(1) : 'Monthly'}
                  </span>
                </div>
                {subscription.currentPeriodEnd && (
                  <div className={styles.planDetail}>
                    <span className={styles.detailLabel}>
                      {subscription.status === 'cancelled' ? 'Access Until' : 'Next Billing'}
                    </span>
                    <span className={styles.detailValue}>
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
              {subscription.tier !== 'free' && subscription.status === 'active' && (
                <button
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={actionLoading}
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>

          {/* Billing Information for paid users */}
          {subscription.tier !== 'free' && (
            <div className={styles.billingSection}>
              <h2 className={styles.sectionTitle}>Billing Information</h2>
              <div className={styles.billingCard}>
                <div className={styles.billingInfo}>
                  <div className={styles.billingItem}>
                    <span className={styles.billingLabel}>Monthly Amount</span>
                    <span className={styles.billingValue}>
                      ${tiers.find(t => t.tier === subscription.tier)?.price || 0}/month
                    </span>
                  </div>
                  {subscription.currentPeriodEnd && (
                    <div className={styles.billingItem}>
                      <span className={styles.billingLabel}>
                        {subscription.status === 'cancelled' ? 'Access Ends' : 'Next Payment'}
                      </span>
                      <span className={styles.billingValue}>
                        {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  <div className={styles.billingItem}>
                    <span className={styles.billingLabel}>Payment Method</span>
                    <span className={styles.billingValue}>Managed by Dodo Payments</span>
                  </div>
                </div>
                <p className={styles.billingNote}>
                  For billing inquiries or to update your payment method, please contact support.
                </p>
              </div>
            </div>
          )}

          {(subscription.tier === 'free' || subscription.status === 'cancelled') && (
            <div className={styles.upgradePlans}>
              <h2 className={styles.sectionTitle}>
                {subscription.status === 'cancelled' ? 'Resubscribe' : 'Upgrade Your Plan'}
              </h2>
              <div className={styles.tiersGrid}>
                {tiers.map((tier, index) => {
                  const isCurrent = tier.tier === subscription.tier && subscription.status === 'active';
                  const isDowngrade = index < currentTierIndex;

                  return (
                    <div
                      key={tier.tier}
                      className={`${styles.tierCard} ${tier.tier === 'professional' ? styles.highlighted : ''}`}
                    >
                      <div className={styles.tierHeader}>
                        <h3 className={styles.tierName}>{tier.name}</h3>
                        <div className={styles.tierPrice}>
                          <span className={styles.currency}>$</span>
                          <span className={styles.price}>{tier.price}</span>
                          <span className={styles.period}>/month</span>
                        </div>
                        <p className={styles.tierDescription}>{tier.description}</p>
                      </div>

                      <ul className={styles.tierFeatures}>
                        {tier.features.map((feature, i) => (
                          <li key={i} className={styles.feature}>
                            <span className={styles.checkmark}>&#10003;</span>
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <button
                        className={`btn ${tier.tier === 'professional' ? 'btn-primary' : 'btn-secondary'} ${styles.tierCta}`}
                        onClick={() => handleUpgrade(tier.tier)}
                        disabled={actionLoading || isCurrent || isDowngrade}
                      >
                        {isCurrent ? 'Current Plan' :
                          isDowngrade ? 'Contact Support' :
                            'Select Plan'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </AuthenticatedLayout>
  );
}
